import React, { RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styles from './NexsetCard.module.scss';
import DataSetDetailActionsContainer from 'js/components/data_sets/cards/DataSetDetailActionsContainer';
import TagsView from 'js/components/elements/tags/TagsView';
import { RESOURCE_TYPE_DATA_SET } from 'js/constants/resourceTypes';
import { isManagedResource, isReadOnly } from 'js/lib/ACLUtils';
import DateUtils from 'js/lib/DateUtils';
import { isDetectedDataset } from 'js/lib/ResourceUtils';
import { NexsetActions } from 'new/components/discover/NexsetActions';
import { ReactComponent as NexsetsIcon } from 'new/components/icons/nexsetsOutline.svg';
import PipelineStatusBadge, {
  Status,
} from 'new/components/pipelines/PipelineStatusChip/PipelineStatusChip';
import { Nexset } from 'new/entities/Nexset';
import { useCurrentUser } from 'new/hooks/adapters/useCurrentUser';
import { useResourceMeta } from 'new/hooks/adapters/useResourceMeta';
import { Box } from 'new/elements/Box';
import { ProfileImage } from 'new/elements/ProfileImage';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/Typography';

interface NexsetCardProps {
  height?: number;
  onClickDetails: (nexset: Nexset) => void;
  onClickRequest?: (nexset: Nexset) => void;
  onClickCancelRequest?: (nexset: Nexset) => () => void;
  nexset: Nexset;
  timeoutEnterRef: any;
  overlayRef: RefObject<HTMLDivElement>;
  owner: {
    email?: string;
    full_name?: string;
    id?: number;
  };
}

export function NexsetCard(props: NexsetCardProps) {
  const { nexset, overlayRef, timeoutEnterRef, onClickDetails, owner } = props;
  const connectionInfo = useResourceMeta(RESOURCE_TYPE_DATA_SET, nexset.id);
  const publicDatasets = useSelector((state: any) => state.publicDatasets);
  const sharedDatasets = useSelector((state: any) => state.sharedDatasets);
  const [showButtons, setShowButtons] = useState(false);
  const timeoutLeaveRef = useRef(null);

  const user = useCurrentUser();
  const heightRef = useRef(0);
  const paperRef = useRef<HTMLDivElement>(null);

  const onMouseEnter = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      clearTimeout(timeoutEnterRef.current);
      clearTimeout(timeoutLeaveRef.current);
      timeoutEnterRef.current = setTimeout(() => {
        setShowButtons(true);
        if (paperRef.current && overlayRef.current) {
          overlayRef.current.className += ' ' + styles.overlay_hover;
          overlayRef.current.style.zIndex = '4';
          paperRef.current.style.zIndex = '5';
          heightRef.current = paperRef.current.offsetHeight;
          paperRef.current.style.width = paperRef.current.offsetWidth + 'px';
          paperRef.current.style.height = 313 + 'px';
          paperRef.current.style.position = 'absolute';
        }
      }, 500);
    },
    [overlayRef],
  );
  const onMouseLeave = useCallback(
    (event: any) => {
      clearTimeout(timeoutEnterRef.current);
      timeoutLeaveRef.current = setTimeout(() => {
        if (paperRef.current && overlayRef.current) {
          overlayRef.current.className = styles.overlay;
          setShowButtons(false);
          paperRef.current.style.height = '260px';
          paperRef.current.style.width = 'unset';
          paperRef.current.style.position = 'relative';
          paperRef.current.style.zIndex = 'unset';
        }
      }, 500);
    },
    [overlayRef, timeoutEnterRef],
  );

  const readOnly = isReadOnly(nexset);
  const isManaged = isManagedResource(nexset);
  const isDetected = isDetectedDataset(nexset);

  const onCloseActions = (e) => {
    onMouseLeave(e);
  };

  const onClick = () => {
    onClickDetails(nexset);
  };

  const shared = useMemo(
    () => publicDatasets.byId.includes(nexset.id) || sharedDatasets.byId.includes(nexset.id),
    [nexset.id, publicDatasets, sharedDatasets],
  );

  return (
    <Box
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      ref={paperRef}
      className={styles.nexsetCard}
    >
      <div className={styles.connectionContainer}>
        <div className={styles.connection}>
          <Typography variant="caption">from</Typography>
          {nexset.is_detected ? <img src={connectionInfo.iconSm} /> : <NexsetsIcon />}
        </div>
        <PipelineStatusBadge
          id={nexset.id}
          revertDirection
          status={nexset.status as Status}
          runtimeStatus={nexset?.runtime_status}
          ingestionMode={nexset.ingestion_mode}
          parentArchivalStatus={nexset?.archival_status}
        />
      </div>
      <Stack flex={1} justifyContent="space-between">
        <Stack flex={1} padding="16px">
          <Stack justifyContent="space-between" flex={1}>
            <Stack>
              <Typography className={styles.title} variant="h6">
                {nexset.name}
              </Typography>
              <Typography className={styles.description} variant="body2">
                {nexset.description}
              </Typography>
            </Stack>
            <Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="overline" className={styles.labelInfo}>
                  CREATED ON
                </Typography>
                <Typography variant="body2">
                  {DateUtils.getDateString(nexset.created_at, 'MMM DD, YYYY')}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="overline" className={styles.labelInfo}>
                  NEXSET ID
                </Typography>
                <Typography variant="body2">{nexset.id}</Typography>
              </Stack>
              {owner?.id !== user?.id ? (
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="overline" className={styles.labelInfo}>
                    OWNER
                  </Typography>
                  <Stack direction="row" gap="4px" alignItems="center">
                    <ProfileImage size="xsmall" fullName={owner?.full_name} />
                    <Typography className={styles.badgeLabel}>{owner?.full_name}</Typography>
                  </Stack>
                </Stack>
              ) : null}
              {nexset.tags && nexset.tags.length ? (
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="overline" className={styles.labelInfo}>
                    TAGS
                  </Typography>
                  <TagsView data={nexset} />
                </Stack>
              ) : null}
            </Stack>
          </Stack>
        </Stack>
        {showButtons ? (
          <Stack
            className={styles.actions}
            direction="row"
            gap="16px"
            justifyContent="space-between"
            alignItems="center"
          >
            <DataSetDetailActionsContainer
              data={nexset}
              showSend={!shared}
              showMenu={!shared}
              readOnly={readOnly}
              onOpenDetailsMenu={onClick}
              TransformComponent={<NexsetActions.Transform />}
              SendComponent={<NexsetActions.Send />}
              showStatus={false}
              isManaged={isManaged}
              isDetected={isDetected}
            />
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}
